import React, { useState } from "react";
import { Button, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { useRequest } from "@components/AppHook";
import { DevForm } from "@components/Forms/Fields";
import { RenderForm } from "@components/Forms/Render";
import { Awesome, DevModal } from "@components/Helpers";
import { DefaultLayout } from "@components/Layouts";
import { ListingCard } from "@components/Pages";
import { url_reverse } from "@components/Utils";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableRow = (props) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: props.id,
    });

    const style = {
        opacity: isDragging ? 0.4 : undefined,
        transform: CSS.Translate.toString(transform),
        transition,
    };

    return (
        <tr ref={setNodeRef} style={style} className={props.className}>
            <td {...listeners} {...attributes}>
                {props.id === props.sortingId ? <Awesome type="spinner" spin /> : <Awesome type="reorder" />}
            </td>
            {props.children}
        </tr>
    );
};

export default function LandingCTBlockScreen() {
    const { t } = useTranslation();
    const { slug } = useParams();
    const apiUrl = url_reverse("api:landing_ct:landing_blocks", { slug });
    const { apiData, setApiData } = useAppApi(apiUrl);
    const { setDelData } = useAppContext();
    const { api_post } = useRequest();
    const [showBlockModal, setShowBlockModal] = useState(false);
    const [sortingId, setSortingId] = useState(null);
    const sensors = useSensors(useSensor(PointerSensor));

    if (!apiData) return null;

    function handleDragEnd(event) {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = apiData.object_list.findIndex((item) => item.id === active.id);
            const newIndex = apiData.object_list.findIndex((item) => item.id === over.id);
            const new_list = arrayMove(apiData.object_list, oldIndex, newIndex);
            const ids = new_list.map((o) => o.id);

            const newApiData = { ...apiData };
            newApiData.object_list = new_list;
            setApiData(newApiData);

            setSortingId(active.id);

            api_post(null, apiUrl, {
                params: { action: "rearrange", ids },
                then: (resp) => {
                    if (resp.status === "ok") {
                        const sorted = { ...apiData };
                        sorted.object_list = resp.object_list;
                        setApiData(sorted);
                    }
                },
                finally: () => setSortingId(null),
            });
        }
    }

    const headerTools = (
        <>
            <Button onClick={() => setShowBlockModal(true)} className="btn btn-sm btn-dark">
                <Awesome type="plus-circle" /> {t("Добавить блок")}
            </Button>
        </>
    );

    const header = (
        <>
            <Link to={url_reverse("admin:pockets:list")}>{t("Карманы")}</Link>
            <span> – </span>
            <Link to={url_reverse("admin:landing_ct:landings")}>{apiData.landing?.name || t("Лендинги")}</Link>
            <span> – </span>
            {t("Блоки")}
        </>
    );

    const addBlockModal = () => {
        return (
            <DevModal
                size="lg"
                show={showBlockModal}
                onHide={() => setShowBlockModal(false)}
                header={t("Добавить блок")}
                body={
                    <RenderForm
                        prefix="main"
                        apiData={apiData}
                        setApiData={setApiData}
                        buttons={
                            <DevForm.Button variant="primary" type="submit" name="action" value="create">
                                {t("Сохранить")}
                            </DevForm.Button>
                        }
                    />
                }
            />
        );
    };

    const tableRow = (item) => {
        return (
            <SortableRow id={item.id} key={item.id} sortingId={sortingId}>
                <td>
                    <Link to={url_reverse("admin:landing_ct:block_items", { slug, block_id: item.id })}>
                        {item.type_label}
                    </Link>
                </td>
                <td>
                    <div className="d-flex justify-content-end">
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => {
                                const deleteUrl = url_reverse("api:landing_ct:landing_blocks:crud", {
                                    slug: apiData.landing.slug,
                                    item_id: item.id,
                                });
                                setDelData({
                                    url: deleteUrl,
                                });
                            }}
                        >
                            <Awesome type="trash-o" />
                        </Button>
                    </div>
                </td>
            </SortableRow>
        );
    };

    return (
        <DefaultLayout>
            <ListingCard header={header} headerTools={headerTools}>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <Table responsive hover className="dmg-table">
                        <thead>
                            <tr>
                                <th style={{ width: "2%" }}></th>
                                <th style={{ width: "80%" }}>{t("Тип")}</th>
                                <th style={{ width: "18%" }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            <SortableContext items={apiData.object_list} strategy={verticalListSortingStrategy}>
                                {apiData.object_list?.map(tableRow)}
                            </SortableContext>
                        </tbody>
                    </Table>
                </DndContext>
                {addBlockModal()}
            </ListingCard>
        </DefaultLayout>
    );
}
